import { motion } from "framer-motion";
import { Check, Boxes, BrainCircuit, Building2, LayoutGrid, ArrowLeftRight } from "lucide-react";
import { Reveal, Pill } from "@/components/landing/primitives";

const ACCENTS = {
  gold: "var(--gold-2)",
  cyan: "var(--cyan)",
  emerald: "var(--emerald)",
};

const ICONS = {
  chain: Boxes,
  ai: BrainCircuit,
  rwa: Building2,
  dapps: LayoutGrid,
  dex: ArrowLeftRight,
};

function Visual({ variant, color }) {
  const common = "w-full h-full";
  if (variant === "chain") {
    return (
      <svg viewBox="0 0 400 300" className={common}>
        <defs>
          <linearGradient id="cg" x1="0" x2="1">
            <stop offset="0" stopColor={color} stopOpacity="0.9" />
            <stop offset="1" stopColor={color} stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3].map((i) => (
          <g key={i} transform={`translate(${40 + i * 90}, 120)`}>
            <rect x="-32" y="-32" width="64" height="64" rx="10" fill="none" stroke="url(#cg)" strokeWidth="1.5" className="animate-floaty" style={{ animationDelay: `${i * 0.4}s` }} />
            <rect x="-20" y="-20" width="40" height="40" rx="6" fill={color} fillOpacity="0.12" />
            <text x="0" y="5" textAnchor="middle" fontSize="11" fontFamily="JetBrains Mono" fill={color}>0{i + 1}</text>
            {i < 3 && <line x1="32" y1="0" x2="58" y2="0" stroke={color} strokeWidth="1.5" className="animate-dash" />}
          </g>
        ))}
      </svg>
    );
  }
  if (variant === "ai") {
    return (
      <svg viewBox="0 0 400 300" className={common}>
        <circle cx="200" cy="150" r="80" fill="none" stroke={color} strokeOpacity="0.2" />
        <circle cx="200" cy="150" r="52" fill="none" stroke={color} strokeOpacity="0.3" />
        <circle cx="200" cy="150" r="6" fill={color} />
        {[...Array(8)].map((_, i) => {
          const a = (i / 8) * Math.PI * 2;
          const x = 200 + Math.cos(a) * 80;
          const y = 150 + Math.sin(a) * 80;
          return (
            <g key={i}>
              <line x1="200" y1="150" x2={x} y2={y} stroke={color} strokeOpacity="0.25" strokeWidth="1" />
              <circle cx={x} cy={y} r="5" fill={color} className="animate-floaty" style={{ animationDelay: `${i * 0.25}s` }} />
            </g>
          );
        })}
        <line x1="120" y1="150" x2="280" y2="150" stroke={color} strokeWidth="1.5" className="animate-dash" />
      </svg>
    );
  }
  if (variant === "rwa") {
    return (
      <svg viewBox="0 0 400 300" className={common}>
        {[[110, 90], [170, 60], [230, 110]].map(([x, h], i) => (
          <g key={i}>
            <rect x={x} y={220 - h} width="46" height={h} fill={color} fillOpacity="0.1" stroke={color} strokeOpacity="0.5" className="animate-floaty" style={{ animationDelay: `${i * 0.3}s` }} />
            {[...Array(Math.floor(h / 22))].map((_, r) => (
              <rect key={r} x={x + 8} y={220 - h + 10 + r * 22} width="10" height="10" fill={color} fillOpacity="0.35" />
            ))}
          </g>
        ))}
        <line x1="80" y1="220" x2="320" y2="220" stroke={color} strokeOpacity="0.4" />
        {[100, 200, 300].map((cx, i) => (
          <circle key={i} cx={cx} cy="255" r="12" fill="none" stroke={color} strokeWidth="1.5" className="animate-floaty" style={{ animationDelay: `${i * 0.5}s` }} />
        ))}
      </svg>
    );
  }
  if (variant === "dapps") {
    return (
      <svg viewBox="0 0 400 300" className={common}>
        {[...Array(9)].map((_, i) => {
          const c = i % 3, r = Math.floor(i / 3);
          return (
            <rect key={i} x={120 + c * 55} y={80 + r * 55} width="44" height="44" rx="10"
              fill={color} fillOpacity={i === 4 ? 0.3 : 0.08} stroke={color} strokeOpacity="0.4"
              className="animate-floaty" style={{ animationDelay: `${i * 0.12}s` }} />
          );
        })}
      </svg>
    );
  }
  // dex
  return (
    <svg viewBox="0 0 400 300" className={common}>
      <circle cx="140" cy="150" r="44" fill={color} fillOpacity="0.1" stroke={color} strokeOpacity="0.5" className="animate-floaty" />
      <circle cx="260" cy="150" r="44" fill={color} fillOpacity="0.1" stroke={color} strokeOpacity="0.5" className="animate-floaty" style={{ animationDelay: "0.5s" }} />
      <text x="140" y="156" textAnchor="middle" fontSize="16" fontFamily="Syne" fontWeight="800" fill={color}>TRT</text>
      <text x="260" y="156" textAnchor="middle" fontSize="13" fontFamily="Syne" fontWeight="800" fill={color}>RWA</text>
      <path d="M180 135 H220" stroke={color} strokeWidth="2" markerEnd="url(#a)" className="animate-dash" />
      <path d="M220 165 H180" stroke={color} strokeWidth="2" className="animate-dash" />
      <defs><marker id="a" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><path d="M0 0 L6 3 L0 6" fill={color} /></marker></defs>
    </svg>
  );
}

export default function FeatureSection({ data, index }) {
  const flip = index % 2 === 1;
  const color = ACCENTS[data.accent] || ACCENTS.gold;
  const Icon = ICONS[data.visual];

  return (
    <section id={data.id} className="relative py-20 md:py-28 scroll-mt-24" data-testid={`section-${data.id}`}>
      <div className="container-trt">
        <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${flip ? "lg:[direction:rtl]" : ""}`}>
          {/* Text */}
          <div className={flip ? "lg:[direction:ltr]" : ""}>
            <Reveal>
              <Pill><span style={{ color }}>●</span> {data.tag}</Pill>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-4 leading-tight">
                {data.title} <span className="gold-text">{data.highlight}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 text-[var(--muted)] leading-relaxed max-w-lg">{data.desc}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-lg">
                {data.points.map((pt) => (
                  <div key={pt.k} className="glass rounded-xl p-4 card-hover">
                    <div className="flex items-center gap-2 text-xs font-mono text-[var(--muted)] uppercase tracking-wide">
                      <Check className="w-3.5 h-3.5" style={{ color }} /> {pt.k}
                    </div>
                    <div className="mt-1.5 font-semibold text-[15px]">{pt.v}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Visual */}
          <Reveal delay={0.1} className={flip ? "lg:[direction:ltr]" : ""}>
            <div className="relative">
              <div
                className="absolute inset-0 blur-3xl opacity-30 rounded-full"
                style={{ background: `radial-gradient(circle at center, ${color}, transparent 65%)` }}
              />
              <div className="relative glass-gold rounded-3xl p-6 aspect-[4/3] flex items-center justify-center overflow-hidden">
                <div className="absolute top-5 left-5 flex items-center gap-2">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}1a`, border: `1px solid ${color}55` }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </span>
                  <span className="font-mono text-xs text-[var(--muted)]">{data.tag}</span>
                </div>
                <Visual variant={data.visual} color={color} />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
